import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Alert, Button, PageHeader, Spinner } from "../../../design-system";
import {
  getDocumentationLevels,
  getInternalDocumentTypes,
} from "../../../helpers/internal_documentation_helper";
import useCurrentMembership from "../../../helpers/useCurrentMembership";
import { unwrap } from "../helpers";
import { LevelsCard } from "./components/LevelsCard";
import { TypesCard } from "./components/TypesCard";

function DocumentacionInternaConfiguracion() {
  document.title = "Niveles y tipos | Kronaly";
  const { can, loading: memLoading } = useCurrentMembership();
  // Gate por capability, no por role. Esto permite que un employee con
  // override `internal_docs:manage_catalogs` acceda a la pantalla.
  const canManageCatalogs = can("internal_docs:manage_catalogs");

  const [levels, setLevels] = useState([]);
  const [types, setTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [levelsRes, typesRes] = await Promise.all([
        getDocumentationLevels(),
        getInternalDocumentTypes(),
      ]);
      setLevels(unwrap(levelsRes) || []);
      setTypes(unwrap(typesRes) || []);
    } catch (e) {
      setError(typeof e === "string" ? e : e?.message || "No se pudo cargar la configuración.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  if (memLoading) {
    return (
      <div className="kr-page" style={{ textAlign: "center", padding: 32 }}>
        <Spinner size={16} />
      </div>
    );
  }

  if (!canManageCatalogs) {
    return (
      <div className="kr-page">
        <PageHeader
          eyebrow="Documentación"
          title="Niveles y tipos"
          description="Configuración del sistema documental corporativo."
        />
        <Alert tone="warning">
          No tienes permiso para gestionar niveles y tipos de documentación.{" "}
          <Link to="/documentacion-interna">Volver a la documentación</Link>.
        </Alert>
      </div>
    );
  }

  return (
    <div className="kr-page">
      <PageHeader
        eyebrow="Documentación"
        title="Niveles y tipos"
        description="Define la pirámide documental (niveles) y la clasificación funcional (tipos) que usarán los documentos internos."
        actions={
          <Link to="/documentacion-interna" style={{ textDecoration: "none" }}>
            <Button variant="ghost" icon="ChevronLeft">
              Volver
            </Button>
          </Link>
        }
      />

      {error && <Alert tone="danger">{error}</Alert>}

      {loading ? (
        <div style={{ padding: 32, textAlign: "center" }}>
          <Spinner size={16} />
        </div>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
          <LevelsCard levels={levels} onChanged={load} />
          <TypesCard types={types} levels={levels} onChanged={load} />
        </div>
      )}
    </div>
  );
}

export default DocumentacionInternaConfiguracion;
